import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { FormActionEntity } from './form-action.entity';
import { FormSubmissionEntity } from './form-submission.entity';

export type FormSubmissionActionStatus =
	| 'pending'
	| 'succeeded'
	| 'skipped'
	| 'retrying'
	| 'failed';

/**
 * One row per action per submission. Unique on (submission, action), which is what makes
 * running an action again idempotent: a re-run reuses what a successful earlier run
 * created instead of creating it twice.
 */
@Index(
	'UK_form_submission_action_submission_action',
	['fkFormSubmissionId', 'fkFormActionId'],
	{ unique: true },
)
@Index('idx_form_submission_action_status', ['status', 'nextAttemptAt'], {})
@Entity('form_submission_action')
export class FormSubmissionActionEntity extends BaseEntity {
	@Column('int', { name: 'fk_form_submission__id', unsigned: true })
	public fkFormSubmissionId!: number;

	@Column('int', { name: 'fk_form_action__id', unsigned: true })
	public fkFormActionId!: number;

	// Copied from the action so the log still reads correctly if the action is removed.
	@Column('varchar', { name: 'actionType', length: 30 })
	public actionType!: string;

	@Column('varchar', { name: 'status', length: 20, default: 'pending' })
	public status!: FormSubmissionActionStatus;

	// Skip reason, ids of what the action created, or a safe error message.
	@Column('json', { name: 'detail', nullable: true })
	public detail!: Record<string, unknown> | null;

	@Column('int', { name: 'attempts', unsigned: true, default: () => "'0'" })
	public attempts!: number;

	@Column('datetime', { name: 'nextAttemptAt', nullable: true })
	public nextAttemptAt!: Date | null;

	@Column('datetime', { name: 'ranAt', nullable: true })
	public ranAt!: Date | null;

	@ManyToOne(() => FormSubmissionEntity, { createForeignKeyConstraints: false })
	@JoinColumn([{ name: 'fk_form_submission__id', referencedColumnName: 'id' }])
	public submission!: FormSubmissionEntity;

	@ManyToOne(() => FormActionEntity, { createForeignKeyConstraints: false })
	@JoinColumn([{ name: 'fk_form_action__id', referencedColumnName: 'id' }])
	public action!: FormActionEntity;
}
